import get from "../../utils/get";
import { HANDLEBARS_REGEX } from "@avenueops/frontend-shared";
import { NotificationType } from "@prisma/client";
import HintIconWithTooltip from "components/HintIconWithTooltip";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js/max";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import IntlTelInput from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import validateNotificationInput from "utils/validateNotificationInput";

interface Props {
  defaultValue: string | number;
  errors: Record<string, unknown>;
  fieldName: string;
  title: string;
  required?: boolean;
  shouldUnregister?: boolean;
  disabled?: boolean;
}

export default function IntlPhoneNumberInput({ defaultValue, errors, fieldName, title, required = false, shouldUnregister, disabled }: Props): JSX.Element {
  const { control } = useFormContext();
  const error = errors && get(errors, `${fieldName}`);

  return (
    <Controller
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      rules={{
        required,
        validate: (value: string | null) => {
          // If no value and not required, return true
          if (!value && !required) return true;

          // If no value and required, return error message
          if (!value) return "Phone number is required";

          return HANDLEBARS_REGEX.test(value) || validateNotificationInput(value, NotificationType.SMS) || "Invalid phone number format";
        },
      }}
      render={({ field: { onChange, onBlur, value: formValue } }) => (
        <div className="flex flex-col w-full">
          <div className="flex w-full gap-1">
            <label className={`mb-1 flex text-sm font-medium leading-5 text-gray-950 ${!title ? "flex-row-reverse" : "gap-x-2"}`}>
              {title ?? null}
              {error ? error.message ? <span className="text-red-500 ">{error.message}</span> : <span className="text-red-500 ">required</span> : null}
            </label>
            <HintIconWithTooltip
              LeadIcon={InformationCircleIcon}
              tooltipText="If you want to pull in phone numbers from data, use Handlebars syntax i.e. {{phone_number}}"
            />
          </div>
          <IntlTelInput
            containerClassName="intl-tel-input w-full"
            inputClassName={`
              form-control
              min-h-[42px]
              form-input block
              w-full rounded
              border border-gray-300 px-3
              py-2 shadow-sm
              transition duration-150 ease-in-out disabled:cursor-not-allowed
              ${error ? "border-red-200 outline-none ring ring-red-200 ring-opacity-50" : ""}
              focus:outline-none
              "focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"}
              text-sm leading-5`}
            onPhoneNumberChange={(isValid, value, selectedCountryData, fullNumber, extension) => {
              // If the regex matches our dynamic input fields, allow that to be passed to the BE
              if (/^{{(.*)}}$/.test(value)) {
                return onChange(value);
              }

              // If we don't detect dynamic fields, then we should try to extract an E164 number as usual
              const countryCode = selectedCountryData.iso2 ?? "US";
              const e164 = parsePhoneNumberFromString(fullNumber, countryCode as CountryCode);
              return onChange(e164?.number);
            }}
            onPhoneNumberBlur={() => onBlur()}
            format={true}
            preferredCountries={["us", "in", "br", "ch"]}
            defaultValue={defaultValue as string}
            disabled={disabled}
          />
        </div>
      )}
    />
  );
}
